import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Button, ReyPopper } from '@framework-controls/core';
import css from './MenuCalendar.module.scss';
import classNames from 'classnames';
import { strings } from 'src/localization';
import { Appointment, Advisor, Transportation } from 'src/models';
import { DateTime } from '@reynolds/datetime';
import { Calendar } from '@framework-controls/calendar';
import AppointmentCard from 'src/components/AppointmentCard/AppointmentCard';
import { ViewMode } from 'src/enums';
// import { convertTime } from 'src/helpers/TimeDisplayHelper/TimeDisplayHelper';

interface IProps {
    appointment: Appointment;
    advisors: Advisor[];
    openFlag: boolean;
    anchorEl: HTMLElement;
    close: (e: any) => void;
    theme: string;
    showCalRef: React.RefObject<HTMLDivElement>
    locale: string;
    transIndex?: Transportation;
}

const MenuCalendar = ((props: IProps) => {
    let VarWidthDefault = '';
    if (props.anchorEl) {
        VarWidthDefault = window.getComputedStyle(props.anchorEl).width;
    }
    const [Varwidth, setWidth] = useState(VarWidthDefault);
    const [checkToplineRight, setToplineRight] = useState(false);

    // Refs are always null in react test renderer, so in order to properly test we need to set a default ref value
    const initialValue: HTMLDivElement = document.createElement('div');
    const menuCalendarRef = useRef<HTMLDivElement>(initialValue);
    const [flyoutWidth, setFlyoutWidth] = useState(props.anchorEl?.offsetWidth);

    // the initial width of the calendar flyout content is zero
    // use setTimeout to get the width of the calendar flyout after it opens.
    useEffect(() => {
        const timer = setTimeout(() => {
            const width = menuCalendarRef.current?.offsetWidth ?? 0;
            setFlyoutWidth(width);
        }, 100);

        return () => clearTimeout(timer);
    }, []);

    // repull offsetWidth on window resize
    const varWidthNumber = parseFloat(Varwidth);
    useEffect(() => {
        const handleResize = () => {
            setWidth(window.getComputedStyle(props.anchorEl).width);
            setFlyoutWidth(menuCalendarRef.current?.offsetWidth);
        };
        if (props.theme === 'toyotaSPM' && varWidthNumber > 406){
            setToplineRight(true);
        } else {
            setToplineRight(false);
        }
        window.addEventListener('resize', handleResize);

        return () => {
            window.removeEventListener('resize', handleResize);
        };
    }, [props.anchorEl, varWidthNumber, props.theme]);

    let width2 = 'calc(100% - ' + Varwidth + ' - 2px)';
    const width3 = (2 + props.anchorEl?.offsetWidth - flyoutWidth);
    const style = getComputedStyle(document.body);
    let varBordercolor = style.getPropertyValue('--IconTextColor');
    let varMargin = '0px';
    // const varWidthNumber = parseFloat(Varwidth);
    if (props.theme === 'toyotaSPM' && varWidthNumber > 410) {
        varBordercolor = style.getPropertyValue('--IconTextColor');
        varMargin = '-2px';
    } else if (varWidthNumber > 421) {
        width2 = '2px';
        varBordercolor = style.getPropertyValue('--menuSelected');
        varMargin = '-2px';
    }

    const { advisor, date, time } = props.appointment;
    const adv = props.advisors.find((x) => x.advisorNum === advisor);
    const appointment = date + ' ' + time.slice(0, 5);
    const apptDate = new DateTime(new Date(appointment));
    // Get time elapsed
    const today = new DateTime(Date.now(), 'EST');
    let minUntil = apptDate.unitsOfTimeBetween('minutes', today);
    if (time.slice(5, 7) === 'pm'){
        minUntil += 720;
    }
    minUntil = Math.abs(minUntil);
    const dayHours = 24;
    const hourMin = 60;
    const dayMinutes = 1440;
    const daysUntil = Math.floor(minUntil / dayMinutes);
    const hoursUntil = Math.floor(minUntil / hourMin) - (dayHours * daysUntil);
    const minutes = minUntil - ((daysUntil * dayMinutes) + (hoursUntil * hourMin));
    // const dayDisplay = daysUntil + ' day(s)';
    // const hourDisplay = hoursUntil + ' hour(s)';
    // const minuteDisplay = minutes + ' minute(s)';
    const dayDisplay = daysUntil + ' ' + strings.DaysLit;
    const hourDisplay = hoursUntil + ' ' + strings.HoursLit;
    const minuteDisplay = minutes + ' ' + strings.MinutesLit;
    let apptDisplay = minuteDisplay;
    if (daysUntil > 0) {
        apptDisplay = dayDisplay + ', ' + hourDisplay + ', ' + minuteDisplay;
    }
    if (daysUntil === 0 && hoursUntil > 0) {
        apptDisplay = hourDisplay + ', ' + minuteDisplay;
    }

    const AdvImgAltTag = adv?.advisorName + ' ' + strings.Photo;

    const handleClickOutside = useCallback((e) => {
        if (!menuCalendarRef.current?.contains(e.target as Element) && !props.showCalRef.current?.contains(e.target as Element)) {
            props.close(e);
        }
    }, [menuCalendarRef, props.close, props.showCalRef]);

    useEffect(() => {
        if (props.openFlag) {
            document.addEventListener('mousedown', handleClickOutside);
        } else {
            document.removeEventListener('mousedown', handleClickOutside);
        }

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [props.openFlag, handleClickOutside]);

    const CalContent = classNames(
        css.CalContent,
        {
            [css.menuMini]: props.theme === 'mini',
        },
    );
    let calLocale: 'en-US' | 'en-CA' | 'fr-CA' = 'en-US';
    if (props.locale === 'en-CA') {
        calLocale = 'en-CA';
    }
    if (props.locale === 'fr-CA') {
        calLocale = 'fr-CA';
    }

    const MenuContent = classNames(
        css.menuContent,
        {
            [css.menuMini]: props.theme === 'mini',
            [css.menuAudi]: props.theme === 'audi',
            [css.menuChevrolet]: props.theme === 'chevrolet',
            [css.menuMB]: props.theme === 'mercedesBenz',
        },
    );

    const closeLitStyles = classNames(
        css.closeLit,
        {
            [css.closeLitBMW]: props.theme === 'bmw',
            [css.fontWeightBaseText]: props.theme === 'ford' || props.theme === 'honda',
            [css.closeLitIN]: props.theme === 'infiniti',
            [css.closeLitMB]: props.theme === 'mercedesBenz',
            [css.closeLitAcura]: props.theme === 'acura',
        },
    );

    const aptInStyles = classNames(
        css.AptIn,
        {
            [css.fontWeightBaseText]: props.theme === 'bmw' || props.theme === 'ford',
        },
    );

    const smallTextStyles = classNames(
        css.smallText,
        {
            [css.fontWeightBaseText]: props.theme === 'bmw' || props.theme === 'ford',
        },
    );


    return (
        // <div className={css.menuCalendarPane}>
        <ReyPopper
            className={css.menuCalendarPane}
            placement='bottom-end'
            anchorEl={props.anchorEl}
            isOpen={props.openFlag} transition>
            {/* <Grow
                style={{ transformOrigin: 'top center' }}
                onHover={props.openFlag}
            > */}
            <div id='CalendarFlyoutContent' className={MenuContent} ref={menuCalendarRef} data-testid='refdiv'>
                <div className={CalContent}>
                    {/* <div className={css.topline}></div>
                    <div className={css.topline2}></div> */}
                    <div className={css.toplineArea}>
                        {checkToplineRight ?
                            (<div className={css.toplineRight} data-testid='toplineRight-div'
                                style = {{width: width3, borderColor: varBordercolor, marginLeft: varMargin}}>
                            </div>) :
                            (<div className={css.toplineLeft} data-testid='toplineLeft-div'
                                style={{width: width2, borderColor: varBordercolor, marginLeft: varMargin}}>
                            </div>)}
                    </div>
                    <div className={css.apptCard}>
                        <AppointmentCard
                            appointment={props.appointment}
                            advisorPhotoPath={props.appointment.advImage}
                            advImgAltTag={AdvImgAltTag}
                            viewMode={ViewMode.Appt}
                            theme={props.theme}
                            transIndex={props.transIndex}
                            locale={props.locale}
                        />
                    </div>
                    <span id='apptIn' className={aptInStyles}>{strings.ApptIn}
                        <div className={smallTextStyles}>{apptDisplay}</div></span>
                    <div className={css.calendar}>
                        <Calendar
                            id='appointment'
                            date={apptDate}
                            // disablePast={true}
                            // outlinedDays={[apptDate]}
                            dayLabelType={props.theme === 'chevrolet' || props.theme === 'honda' ||
                                props.theme === 'buick' ? 'char' : 'abbr'}
                            // locale='en-US'
                            locale={calLocale}
                            selectedDayLiteral={strings.YourApptDate}
                            readOnly
                            size='large'
                        />
                    </div>
                    <div className={css.closeBtn}>
                        <Button
                            variant={'text'}
                            id='closeBtn'
                            onClick={props.close}>
                            <div className={closeLitStyles}>{strings.Close}</div>
                        </Button>
                    </div>
                </div>
            </div>
            {/* </Grow> */}
        </ReyPopper>
    );
});

export default MenuCalendar;